/*
 * specRef — turn a human spec reference into a deep-link to the exact source.
 *
 * The console scatters references like "Part IV §27 — RFC 9421 signed dispatch",
 * "Plane III · §15–§23", "Design §8", or "RFC 6962 §2.1.1 · PART III §16.4".
 * parseSpecRef() tokenises such a string into plain-text and link segments so a
 * renderer can make ONLY the reference tokens clickable while leaving the prose
 * ("— signed dispatch", "· ", "LIVE") untouched. A single string may contain
 * several independent targets (a repo Part AND an external RFC), so we cannot
 * wrap the whole string in one anchor.
 *
 * Targets:
 *   - Part/Plane <roman> §<sec>  → the repo Part file at the section's heading
 *   - Design/Operator §<sec>     → the no-code provisioning design doc
 *   - RFC <n> §<sec>             → rfc-editor.org (not in this repo)
 *
 * Anchors come from anchors.generated.ts (built from the real markdown), so a
 * link always lands on the exact heading GitHub renders for that section.
 */

import { SPEC_ANCHORS } from "./anchors.generated";

/** GitHub blob base for the spec repo. Override per-deployment via env. */
const REPO_BASE = (
  (import.meta.env?.VITE_SPEC_REPO_BASE as string | undefined) ??
  "https://github.com/UNCTAD-OpenGov/opengov-mesh/blob/main"
).replace(/\/$/, "");

/*
 * Link kill-switch. The spec repo is currently PRIVATE, so deep-links 404 for
 * any console viewer who is not a GitHub collaborator. Deployments that face a
 * non-collaborator audience set VITE_SPEC_LINKS_PUBLIC="false" to render the
 * references as styled-but-plain text (no dead links). Flip to the default
 * (links on) once VITE_SPEC_REPO_BASE points at a publicly reachable mirror.
 */
const LINKS_ENABLED =
  ((import.meta.env?.VITE_SPEC_LINKS_PUBLIC as string | undefined) ?? "true") !== "false";

export interface SpecRefSegment {
  /** The exact substring to render. */
  text: string;
  /** Resolved deep-link, or undefined for plain prose. */
  href?: string;
  /** Hover/aria hint, e.g. "Open Part IV §27 on GitHub". */
  title?: string;
}

/*
 * One scanner regex with three alternatives. The display text of a match is the
 * whole matched substring (range included); only the FIRST section drives the
 * anchor. Roman numerals are the doc key for both "Part" and "Plane" forms.
 */
const SCAN =
  /(?<partKind>Part|PART|Plane|PLANE)\s+(?<roman>[IVX]+)(?:\s*·\s*)?\s*§\s*(?<partSec>\d+(?:\.\d+)*)(?:\s*[–-]\s*§?\s*\d+(?:\.\d+)*)?|(?<designKind>Design|Operator)(?:\s*·\s*)?\s*§\s*(?<designSec>\d+(?:\.\d+)*)|RFC\s+(?<rfcNum>\d+)(?:\s+§\s*(?<rfcSec>\d+(?:\.\d+)*))?/g;

function repoUrl(docKey: string, section: string): string | undefined {
  const doc = SPEC_ANCHORS[docKey];
  if (!doc) return undefined;
  const anchor = doc.anchors[section];
  // Only emit a link when we can land on the EXACT heading. An unindexed
  // section degrades to plain text (via the !href branch in parseSpecRef)
  // rather than a misleading file-top link whose title claims the section.
  if (!anchor) return undefined;
  return `${REPO_BASE}/${doc.file}#${anchor}`;
}

function rfcUrl(num: string, section?: string): string {
  const frag = section ? `#section-${section}` : "";
  return `https://www.rfc-editor.org/rfc/rfc${num}${frag}`;
}

/**
 * Split a spec-ref string into ordered segments. Non-reference text (and any
 * reference that cannot be resolved) is returned as plain segments, so the
 * concatenation of `seg.text` always reproduces the input verbatim.
 */
export function parseSpecRef(text: string): SpecRefSegment[] {
  if (!text) return [];
  // Links disabled for this deployment → the whole string is plain text.
  if (!LINKS_ENABLED) return [{ text }];
  const segments: SpecRefSegment[] = [];
  let last = 0;
  SCAN.lastIndex = 0;
  for (let m = SCAN.exec(text); m !== null; m = SCAN.exec(text)) {
    const g = m.groups!;
    let href: string | undefined;
    let title: string | undefined;

    if (g.partKind) {
      href = repoUrl(g.roman, g.partSec);
      title = href ? `Open ${m[0]} on GitHub` : undefined;
    } else if (g.designKind) {
      href = repoUrl("DESIGN", g.designSec);
      title = href ? `Open the design doc §${g.designSec} on GitHub` : undefined;
    } else if (g.rfcNum) {
      href = rfcUrl(g.rfcNum, g.rfcSec);
      title = `Open RFC ${g.rfcNum}${g.rfcSec ? ` §${g.rfcSec}` : ""} (rfc-editor.org)`;
    }

    // Unresolvable references stay as plain text; don't break the run.
    if (!href) continue;

    if (m.index > last) segments.push({ text: text.slice(last, m.index) });
    segments.push({ text: m[0], href, title });
    last = m.index + m[0].length;
  }
  if (last < text.length) segments.push({ text: text.slice(last) });
  return segments;
}

/** True when the string contains at least one resolvable reference. */
export function hasSpecLink(text: string): boolean {
  return parseSpecRef(text).some((s) => s.href);
}
